import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonCol,
  IonInput,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonContent,
  IonRow,
  IonSpinner,
  IonBadge,
  IonTextarea,
  IonToolbar,
  IonAlert,
  IonLoading,
  IonToast
} from '@ionic/react';
import {
  trash,
  download,
  imageOutline,
  documentOutline,
  closeCircleOutline,
  informationCircleOutline,
  checkmark,
  checkmarkCircle,
  camera as cameraIcon,
  videocam,
  cloudUploadOutline,
  returnDownBack,
  createOutline
} from 'ionicons/icons';

import { Capacitor, registerPlugin } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { useJobMedia } from '@/hooks/useJobMedia';
import type { JobMediaItem, JobMediaStats } from '@rsmaids/shared/types/jobMedia';
import { UploadProgress } from './UploadProgress';
import styles from './JobMediaManager.module.scss';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../i18n/locale';

// Custom CameraX plugin — runs camera INSIDE the Activity (no Activity switch = no process death)
interface InlineCameraPlugin {
  takePhoto(options?: { videoQuality?: 'SD' | 'HD' | 'FHD' | 'UHD' }): Promise<{ filePath: string; webPath: string; format: string; mediaType?: string }>;
  // startVideoRecording opens camera and returns when recording finishes (user stops it)
  startVideoRecording(options?: { videoQuality?: 'SD' | 'HD' | 'FHD' | 'UHD' }): Promise<{ filePath: string; webPath: string; format: string; mediaType?: string }>;
  // stopVideoRecording is for external control (if needed)
  stopVideoRecording(): Promise<void>;
}
// Use existing registration if already registered (avoids HMR duplicate warning)
const InlineCamera: InlineCameraPlugin =
  (window as unknown as { Capacitor?: { Plugins?: Record<string, unknown> } })?.Capacitor?.Plugins?.['InlineCamera'] as InlineCameraPlugin
  ?? registerPlugin<InlineCameraPlugin>('InlineCamera');

interface JobMediaManagerProps {
  jobId: string;
  collectionId: string;
  onMediaChange?: (media: JobMediaItem[]) => void;
  maxFiles?: number;
  acceptedFileTypes?: string;
  title?: string;
  compact?: boolean;
}

export const JobMediaManager: React.FC<JobMediaManagerProps> = ({
  jobId,
  collectionId,
  onMediaChange,
  maxFiles = 50,
  acceptedFileTypes = 'image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx',
  title = 'Job Media',
  compact = false,
}) => {
  const { t: tr } = useTranslation();
  const { media, loading, error, upload, delete: deleteMedia, update, getStats } = useJobMedia({
    collectionId,
    jobId,
    autoFetch: true
  });

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<JobMediaItem | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [showVideoToast, setShowVideoToast] = useState(false);
  // Track blob URL in ref for safe cleanup (state update is async, ref is synchronous)
  const videoBlobRef = useRef<string | null>(null);

  const [videoReady, setVideoReady] = useState(false);

  // Resetting video state when the active video/detail-panel target changes
  // is computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as setState calls inside the effect
  // below, which now only handles the actual fetch/blob-URL work and its
  // resource cleanup.
  const isVideoActive = Boolean(selectedMedia?.mediaType === 'video' && showDetails && selectedMedia.fileUrl);
  const [prevVideoResetKey, setPrevVideoResetKey] = useState({ id: selectedMedia?._id, fileUrl: selectedMedia?.fileUrl, showDetails });
  const videoResetKey = { id: selectedMedia?._id, fileUrl: selectedMedia?.fileUrl, showDetails };
  if (
    prevVideoResetKey.id !== videoResetKey.id ||
    prevVideoResetKey.fileUrl !== videoResetKey.fileUrl ||
    prevVideoResetKey.showDetails !== videoResetKey.showDetails
  ) {
    setPrevVideoResetKey(videoResetKey);
    setVideoBlobUrl(null);
    setVideoReady(false);
    setVideoError(null);
    if (isVideoActive) setShowVideoToast(false);
  }

  useEffect(() => {
    if (selectedMedia?.mediaType === 'video' && showDetails && selectedMedia.fileUrl) {
      let cancelled = false;

      fetch(selectedMedia.fileUrl)
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then(blob => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          videoBlobRef.current = url;
          setVideoBlobUrl(url);
        })
        .catch(() => {
          if (cancelled) return;
          setVideoError(tr('components.JobMediaManager.unableToLoadThisVideo'));
          setShowVideoToast(true);
        });

      return () => {
        cancelled = true;
        if (videoBlobRef.current) {
          URL.revokeObjectURL(videoBlobRef.current);
          videoBlobRef.current = null;
        }
      };
    } else if (videoBlobRef.current) {
      URL.revokeObjectURL(videoBlobRef.current);
      videoBlobRef.current = null;
    }
  }, [selectedMedia?._id, selectedMedia?.fileUrl, selectedMedia?.mediaType, showDetails, tr]);
  const [editDescription, setEditDescription] = useState('');
  const [editFileName, setEditFileName] = useState('');
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [textareaFocused, setTextareaFocused] = useState(false);
  const [descriptionActive, setDescriptionActive] = useState(false);
  const modalContentRef = useRef<HTMLIonContentElement>(null);
  const descriptionRef = useRef<HTMLIonTextareaElement>(null);

  useEffect(() => {
    if (descriptionActive && descriptionRef.current) {
      descriptionRef.current.getInputElement().then(el => {
        el.focus();
        el.selectionStart = el.selectionEnd = el.value.length;
      });
    }
  }, [descriptionActive]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const showListener = Keyboard.addListener('keyboardWillShow', info => {
      setKeyboardHeight(info.keyboardHeight);
      document.documentElement.style.setProperty('--kbd-height', `${info.keyboardHeight}px`);
      const focused = document.activeElement;
      if (focused) {
        const rect = focused.getBoundingClientRect();
        const visibleBottom = window.innerHeight - info.keyboardHeight;
        if (rect.bottom > visibleBottom) {
          modalContentRef.current?.scrollByPoint(0, rect.bottom - visibleBottom + 16, 150);
        }
      }
    });
    const hideListener = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardHeight(0);
      document.documentElement.style.removeProperty('--kbd-height');
    });
    return () => { showListener.then(l => l.remove()); hideListener.then(l => l.remove()); };
  }, []);
  const [stats, setStats] = useState<JobMediaStats | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetName, setDeleteTargetName] = useState<string>('');
  const [deleting, setDeleting] = useState(false);
  const [deleteToast, setDeleteToast] = useState<{ isOpen: boolean; message: string }>({ isOpen: false, message: '' });
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggered = useRef(false);
  const [uploadingFiles, setUploadingFiles] = useState<Array<{
    file: File;
    progress: number;
    status: 'uploading' | 'completed' | 'error';
    error?: string;
    timeRemaining?: number;
    uploadStartTime?: number;
    bytesUploaded?: number;
    compressedSize?: number;
  }>>([]);

  // Update parent when media changes
  useEffect(() => {
    if (onMediaChange) {
      onMediaChange(media);
    }
  }, [media, onMediaChange]);

  // Auto-clear completed uploads after 3 seconds to show Recently Uploaded
  useEffect(() => {
    const allCompleted = uploadingFiles.length > 0 && uploadingFiles.every(f => f.status === 'completed');
    if (allCompleted) {
      const timer = setTimeout(() => {
        setUploadingFiles([]);
        setUploading(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [uploadingFiles]);

  const loadStats = useCallback(async () => {
    try {
      // Only load stats if we have media
      if (media.length > 0) {
        const statsData = await getStats();
        setStats(statsData);
      } else {
        setStats(null);
      }
    } catch {
      // stats are optional, silently ignore
      setStats(null);
    }
  }, [media, getStats]);

  // Load statistics
  useEffect(() => {
    Promise.resolve().then(() => loadStats());
  }, [loadStats]);

  // Multi-photo queue — tap Photo to keep adding, tap Done to upload all
  const [burstQueue, setBurstQueue] = useState<File[]>([]);

  // Handle file selection
  const handleFileSelect = useCallback(async (files: FileList | File[] | null, uploadPhotosImmediately = false) => {
    if (!files || files.length === 0) return;

    // Check file count limit
    if (media.length + files.length > maxFiles) {
      alert(tr('components.JobMediaManager.cannotExceedFilesCurrentTryingTo', { maxFiles, length: media.length, length2: files.length }));
      return;
    }

    // Separate videos from photos based on actual file MIME type, not cameraMode
    const videos: File[] = [];
    const photos: File[] = [];
    
    Array.from(files).forEach(file => {
      if (file.type.startsWith('video/')) {
        videos.push(file);
      } else {
        photos.push(file);
      }
    });

    // If we have videos, upload them immediately (video mode)
    if (videos.length > 0) {
      const newUploadingFiles = videos.map(file => ({
        file,
        progress: 0,
        status: 'uploading' as const,
        uploadStartTime: Date.now(),
        bytesUploaded: 0
      }));
      
      setUploadingFiles(prev => [...prev, ...newUploadingFiles]);
      setUploading(true);

      try {
        for (const file of videos) {
          // Validate file size (50MB max per file)
          if (file.size > 50 * 1024 * 1024) {
            alert(tr('components.JobMediaManager.fileIsTooLargeMax50mb', { name: file.name }));
            setUploadingFiles(prev => {
              const updated = [...prev];
              const fileIndex = updated.findIndex(f => f.file === file);
              if (fileIndex > -1) {
                updated[fileIndex] = { ...updated[fileIndex], status: 'error', error: 'File too large' };
              }
              return updated;
            });
            continue;
          }

          try {
            const result = await upload(file, undefined, (compressedSize) => {
              setUploadingFiles(prev => {
                const updated = [...prev];
                const fileIndex = updated.findIndex(f => f.file === file);
                if (fileIndex > -1) updated[fileIndex] = { ...updated[fileIndex], compressedSize };
                return updated;
              });
            });
            setUploadingFiles(prev => {
              const updated = [...prev];
              const fileIndex = updated.findIndex(f => f.file === file);
              if (fileIndex > -1) {
                updated[fileIndex] = { ...updated[fileIndex], status: 'completed', progress: 100, compressedSize: result.fileSize };
              }
              return updated;
            });
          } catch (err) {
            setUploadingFiles(prev => {
              const updated = [...prev];
              const fileIndex = updated.findIndex(f => f.file === file);
              if (fileIndex > -1) {
                updated[fileIndex] = { ...updated[fileIndex], status: 'error', error: String(err) };
              }
              return updated;
            });
          }
        }
      } finally {
        setUploading(false);
      }
    }

    // If we have photos, either upload immediately or add to burst queue
    if (photos.length > 0) {
      if (uploadPhotosImmediately) {
        // Uploading from burst queue — upload immediately like videos
        const newUploadingFiles = photos.map(file => ({
          file,
          progress: 0,
          status: 'uploading' as const,
          uploadStartTime: Date.now(),
          bytesUploaded: 0
        }));
        
        setUploadingFiles(prev => [...prev, ...newUploadingFiles]);
        setUploading(true);

        try {
          for (const file of photos) {
            // Validate file size (50MB max per file)
            if (file.size > 50 * 1024 * 1024) {
              alert(tr('components.JobMediaManager.fileIsTooLargeMax50mb', { name: file.name }));
              setUploadingFiles(prev => {
                const updated = [...prev];
                const fileIndex = updated.findIndex(f => f.file === file);
                if (fileIndex > -1) {
                  updated[fileIndex] = { ...updated[fileIndex], status: 'error', error: 'File too large' };
                }
                return updated;
              });
              continue;
            }

            try {
              const result = await upload(file, undefined, (compressedSize) => {
                setUploadingFiles(prev => {
                  const updated = [...prev];
                  const fileIndex = updated.findIndex(f => f.file === file);
                  if (fileIndex > -1) updated[fileIndex] = { ...updated[fileIndex], compressedSize };
                  return updated;
                });
              });
              setUploadingFiles(prev => {
                const updated = [...prev];
                const fileIndex = updated.findIndex(f => f.file === file);
                if (fileIndex > -1) {
                  updated[fileIndex] = { ...updated[fileIndex], status: 'completed', progress: 100, compressedSize: result.fileSize };
                }
                return updated;
              });
            } catch (err) {
              setUploadingFiles(prev => {
                const updated = [...prev];
                const fileIndex = updated.findIndex(f => f.file === file);
                if (fileIndex > -1) {
                  updated[fileIndex] = { ...updated[fileIndex], status: 'error', error: String(err) };
                }
                return updated;
              });
            }
          }
        } finally {
          setUploading(false);
        }
      } else {
        // From camera input — add to burst queue for user to review before uploading
        setBurstQueue(prev => [...prev, ...photos]);
      }
    }
  }, [media.length, maxFiles, upload, tr]);

  // Handle drag and drop
  const handleDrag = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Handle file input change — desktop file picker always uploads immediately (no burst queue)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files, true);
  };

  // Truncate long filenames for display
  const truncateFilename = (name: string, maxLen = 32) => {
    if (name.length <= maxLen) return name;
    const ext = name.lastIndexOf('.') > -1 ? name.slice(name.lastIndexOf('.')) : '';
    const base = name.slice(0, name.length - ext.length);
    const keep = maxLen - ext.length - 3;
    return base.slice(0, Math.ceil(keep / 2)) + '…' + base.slice(-Math.floor(keep / 2)) + ext;
  };

  // Handle delete - show confirmation alert
  const handleDelete = (mediaItemId: string, fileName?: string) => {
    setDeleteTargetId(mediaItemId);
    setDeleteTargetName(fileName || 'this file');
    setShowDeleteAlert(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    setShowDeleteAlert(false);
    setDeleting(true);
    let count = 0;
    if (selectionMode && selectedIds.size > 0) {
      count = selectedIds.size;
      try {
        for (const id of selectedIds) {
          await deleteMedia(id);
        }
      } catch {
        // bulk delete error handled by useJobMedia hook
      }
      setSelectedIds(new Set());
      setSelectionMode(false);
    } else if (deleteTargetId) {
      count = 1;
      try {
        await deleteMedia(deleteTargetId);
      } catch {
        // delete error handled by useJobMedia hook
      }
    }
    setDeleteTargetId(null);
    setDeleting(false);
    setDeleteToast({ isOpen: true, message: `${count} file${count !== 1 ? 's' : ''} deleted` });
  };

  // Long press to enter selection mode
  const handlePointerDown = useCallback((itemId: string) => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      setSelectionMode(true);
      setSelectedIds(new Set([itemId]));
    }, 500);
  }, []);

  const handlePointerUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const toggleSelection = useCallback((itemId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      // Exit selection mode if nothing selected
      if (next.size === 0) {
        setSelectionMode(false);
      }
      return next;
    });
  }, []);

  const cancelSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return;
    setDeleteTargetName(`${selectedIds.size} file${selectedIds.size > 1 ? 's' : ''}`);
    setShowDeleteAlert(true);
  }, [selectedIds]);

  // Handle update description
  const handleUpdateDescription = async () => {
    if (!selectedMedia || !selectedMedia._id) return;
    try {
      let meta: Record<string, string> = {};
      try { meta = JSON.parse(selectedMedia.metadata || '{}'); } catch { /* ignore */ }
      meta.updatedTimestamp = new Date().toISOString();
      await update(selectedMedia._id, { description: editDescription, fileName: editFileName, metadata: JSON.stringify(meta) });
      setShowDetails(false);
      setSelectedMedia(null);
      setEditDescription('');
      setEditFileName('');
    } catch {
      // update error handled by useJobMedia hook
    }
  };

  // Get media icon based on type (returns emoji like UploadProgress)
  const getMediaIcon = (mediaType: string) => {
    switch (mediaType) {
      case 'image':
        return '🖼️';
      case 'video':
        return '🎬';
      case 'audio':
        return '🎵';
      case 'document':
      case 'text':
        return '📄';
      default:
        return '📎';
    }
  };



  // Hidden file input for video capture only
  const videoInputRef = useRef<HTMLInputElement>(null);
  
  // Hidden file input for photo capture (web/PWA only)
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Set capture="environment" on video input to open camcorder directly
  // But NOT on photo input for Android PWA (capture attr crashes Android PWA)
  useEffect(() => {
    const isAndroidPWA = !Capacitor.isNativePlatform() && /android/i.test(navigator.userAgent);
    videoInputRef.current?.setAttribute('capture', 'environment');
    // Only set capture on photo input for iOS PWA / Desktop - not Android PWA
    if (!isAndroidPWA && photoInputRef.current) {
      photoInputRef.current.setAttribute('capture', 'environment');
    }
  }, []);

  // Track which camera mode was selected: 'photo' or 'video'
  // Used to route the camera result to the correct handler
  const [cameraMode, setCameraMode] = useState<'photo' | 'video' | null>(null);

  // Use CameraX inline plugin — camera renders as native overlay ON TOP of WebView.
  // No Activity switch. No intent. No process death. 100% guaranteed.
  // Preview overlay is shown natively in the camera plugin.
  const addPhotoToQueue = useCallback(async () => {
    // isNativePlatform() returns true ONLY when running inside Capacitor native shell (APK/IPA)
    // It returns false for PWA installed from browser, even on Android/iOS
    const isNativeApp = Capacitor.isNativePlatform();
    const isAndroidPWA = !isNativeApp && /android/i.test(navigator.userAgent);
    if (isNativeApp) {
      // Native app (APK/IPA): use inline camera plugin
      setCameraMode('photo');
      try {
        const image = await InlineCamera.takePhoto();

        if (image.webPath) {
          // Fetch the image from the file URI and convert to File
          const response = await fetch(Capacitor.convertFileSrc(image.filePath));
          const blob = await response.blob();
          const timestamp = Date.now();
          
          // Check if user switched to VIDEO mode inside camera and recorded a video instead
          const isVideo = image.mediaType === 'video' || image.format === 'mp4';
          
          const file = new File(
            [blob],
            isVideo ? `video_${timestamp}.${image.format || 'mp4'}` : `photo_${timestamp}.${image.format || 'jpeg'}`,
            { type: isVideo ? `video/${image.format || 'mp4'}` : `image/${image.format || 'jpeg'}` }
          );
          if (isVideo) {
            handleFileSelect([file]);
          } else {
            setBurstQueue(prev => [...prev, file]);
          }
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes('cancelled') || message.includes('User cancelled')) {
          return;
        }
        alert('Camera error: ' + message);
      } finally {
        setCameraMode(null);
      }
    } else if (isAndroidPWA) {
      // Android PWA: MUST use PWA Elements modal to keep app in foreground
      // Native camera (via file input) kills the PWA app on Android
      // We directly use the pwa-camera-modal element to avoid Capacitor Camera's fallback behavior
      setCameraMode('photo');
      try {
        // Verify pwa-camera-modal is registered
        if (!customElements.get('pwa-camera-modal')) {
          alert(tr('components.JobMediaManager.cameraNotAvailablePleaseReloadThe'));
          setCameraMode(null);
          return;
        }
        
        // Create and use the camera modal directly (bypassing Capacitor Camera plugin)
        const cameraModal = document.createElement('pwa-camera-modal') as unknown as HTMLElement & {
          facingMode: string;
          present: () => Promise<void>;
          dismiss: () => Promise<void>;
          componentOnReady: () => Promise<HTMLElement>;
        };
        cameraModal.facingMode = 'environment';
        document.body.appendChild(cameraModal);
        
        await cameraModal.componentOnReady();
        
        // Wait for photo via event — pwa-camera-modal's onPhoto detail is a
        // Blob, not a data URL string (despite the plugin's own type surface
        // suggesting otherwise). Passing it to fetch() as if it were a URL
        // silently fetches the current page instead of erroring, so detect
        // the Blob case explicitly rather than assume a string.
        const photo = await new Promise<Blob | string>((resolve, reject) => {
          cameraModal.addEventListener('onPhoto', ((e: CustomEvent) => {
            const photoData = e.detail;
            if (photoData === null) {
              reject(new Error('User cancelled'));
            } else if (photoData instanceof Error) {
              reject(photoData);
            } else {
              resolve(photoData as Blob | string);
            }
            cameraModal.dismiss();
            document.body.removeChild(cameraModal);
          }) as EventListener);

          cameraModal.present();
        });

        const blob = photo instanceof Blob ? photo : await (await fetch(photo)).blob();
        const timestamp = Date.now();
        const file = new File(
          [blob],
          `photo_${timestamp}.jpeg`,
          { type: 'image/jpeg' }
        );
        setBurstQueue(prev => [...prev, file]);
        
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes('cancelled') || message.includes('User cancelled')) {
          return;
        }
        // Show error to user - do NOT fall back to native camera
        alert(tr('components.JobMediaManager.cameraErrorPleaseEnsureCameraPermissions', { message }));
      } finally {
        setCameraMode(null);
      }
    } else {
      // iOS PWA / Desktop: use HTML file input with camera capture
      setCameraMode('photo');
      photoInputRef.current?.click();
    }
  }, [handleFileSelect, tr]);

  // Record video — use inline camera on native, file input on web
  const recordVideo = useCallback(async () => {
    const isNative = Capacitor.isNativePlatform();
    
    if (isNative) {
      // Native: use inline camera plugin in VIDEO mode
      setCameraMode('video');
      try {
        // This promise resolves when user accepts/rejects the preview in the native overlay
        const video = await InlineCamera.startVideoRecording();
        
        if (video && video.filePath) {
          // Fetch the video from the file URI and convert to File
          const response = await fetch(Capacitor.convertFileSrc(video.filePath));
          const blob = await response.blob();
          const timestamp = Date.now();
          
          // Check if this is actually a photo (user switched to PHOTO mode after opening via video button)
          const isPhoto = video.mediaType === 'photo' || video.format === 'jpeg' || video.format === 'jpg';
          
          const file = new File(
            [blob],
            isPhoto ? `photo_${timestamp}.${video.format || 'jpeg'}` : `video_${timestamp}.${video.format || 'mp4'}`,
            { type: isPhoto ? `image/${video.format || 'jpeg'}` : `video/${video.format || 'mp4'}` }
          );
          if (isPhoto) {
            setBurstQueue(prev => [...prev, file]);
          } else {
            handleFileSelect([file]);
          }
          setCameraMode(null);
        }
      } catch (err: unknown) {
        // User cancelled — not an error
        const message = err instanceof Error ? err.message : String(err);
        setCameraMode(null);
        if (message.includes('cancelled') || message.includes('User cancelled')) {
          return;
        }
      }
    } else {
      // Web: use HTML file input
      setCameraMode('video');
      videoInputRef.current?.click();
    }
  }, [handleFileSelect]);

  const uploadQueue = useCallback(() => {
    const queue = [...burstQueue];
    setBurstQueue([]);
    setCameraMode(null);
    if (queue.length > 0) {
      handleFileSelect(queue, true);
    }
  }, [burstQueue, handleFileSelect]);

  const handleVideoFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && cameraMode === 'video') {
      // Video — upload immediately
      handleFileSelect(files);
    }
    setCameraMode(null);
    // Reset so the same file can be re-selected
    if (e.target) e.target.value = '';
  }, [cameraMode, handleFileSelect]);

  // Handle photo file from web/PWA camera
  const handlePhotoFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && cameraMode === 'photo') {
      // Photo — add to burst queue
      const file = files[0];
      setBurstQueue(prev => [...prev, file]);
    }
    setCameraMode(null);
    // Reset so the same file can be re-selected
    if (e.target) e.target.value = '';
  }, [cameraMode]);

  return (
    <>
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={handleVideoFileChange}
        className={styles['hidden-camera-input']}
        aria-label={tr('components.JobMediaManager.recordVideo')}
      />
      {/* Photo input only for iOS PWA / Desktop - Android PWA uses Camera.getPhoto with PWA Elements */}
      {!(!Capacitor.isNativePlatform() && /android/i.test(navigator.userAgent)) && (
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          onChange={handlePhotoFileChange}
          className={styles['hidden-camera-input']}
          aria-label={tr('components.JobMediaManager.takePhoto')}
        />
      )}

      <IonCard className={`${styles['media-manager']}${compact ? ` ${styles.compact}` : ''}`}>
        <IonCardHeader>
          <IonCardTitle>
            {title}
            {stats && (
              <IonBadge color="primary" slot="end">
                {stats.totalCount}
              </IonBadge>
            )}
          </IonCardTitle>
        </IonCardHeader>

        <IonCardContent>
          {error && (
            <IonItem lines="none" color="danger">
              <IonIcon icon={informationCircleOutline} slot="start" />
              <IonLabel>{error}</IonLabel>
            </IonItem>
          )}

          {/* Upload Area */}
          <IonCard className={`${styles['upload-area']} ${dragActive ? styles['active'] : ''} ${compact ? styles.compact : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <IonCardContent>
              <input
                type="file"
                multiple
                accept={acceptedFileTypes}
                onChange={handleInputChange}
                className={styles['file-input']}
                id={`media-input-${jobId}`}
                aria-label={tr('components.JobMediaManager.uploadMediaFiles')}
                disabled={uploading || loading || media.length >= maxFiles}
              />
              <input
                type="file"
                multiple
                accept={acceptedFileTypes}
                onChange={handleInputChange}
                className={styles['file-input']}
                id={`media-input-existing-${jobId}`}
                aria-label={tr('components.JobMediaManager.uploadMoreMediaFiles')}
                disabled={uploading || loading || media.length >= maxFiles}
              />
              
              {/* Action buttons — Mobile: Photo + Video | Desktop: Upload */}
              <IonCard className={styles['upload-actions']}>
                <IonCardContent>
                  {isMobile ? (
                    <>
                      <div className={styles.splitButton}>
                        <IonButton
                          onClick={addPhotoToQueue}
                          disabled={uploading || loading || media.length >= maxFiles}
                          className={styles.splitLeft}
                          fill="clear"
                        >
                          {burstQueue.length === 0 && <IonIcon icon={cameraIcon} />}{tr('components.JobMediaManager.photo')}{burstQueue.length > 0 ? ` (${burstQueue.length})` : ''}
                        </IonButton>
                        {burstQueue.length > 0 && (
                          <IonButton
                            onClick={uploadQueue}
                            className={styles.splitRight}
                            fill="clear"
                          >
                            <IonIcon slot="icon-only" icon={checkmark} />
                          </IonButton>
                        )}
                      </div>
                      <IonBadge>{media.length}/{maxFiles}</IonBadge>
                      <IonButton
                        onClick={recordVideo}
                        className={styles.cameraButton}
                        disabled={uploading || loading || media.length >= maxFiles}
                        title={tr('components.JobMediaManager.recordVideo')}
                      >
                        <IonIcon icon={videocam} />{tr('components.JobMediaManager.video')}
                      </IonButton>
                    </>
                  ) : (
                    <>
                      <IonButton
                        onClick={() => setShowFileUpload(!showFileUpload)}
                        className={styles.cameraButton}
                        disabled={uploading || loading || media.length >= maxFiles}
                        title={tr('components.JobMediaManager.toggleFileUpload')}
                      >
                        <IonIcon icon={showFileUpload ? closeCircleOutline : cloudUploadOutline} />
                        {showFileUpload ? tr('components.JobMediaManager.hide') : tr('components.JobMediaManager.upload')}
                      </IonButton>
                      <IonBadge>{media.length}/{maxFiles}</IonBadge>
                    </>
                  )}
                </IonCardContent>
              </IonCard>

              {/* Desktop: drag & drop area — toggled by Upload button */}
              {!isMobile && showFileUpload && (
                <IonCard className={styles['conditional-file-upload']}>
                  <IonCardContent
                    onClick={() => document.getElementById(`media-input-${jobId}`)?.click()}
                    className={styles['upload-label']}
                  >
                    <IonIcon icon={cloudUploadOutline} />
                    <p>{tr('components.JobMediaManager.dragDropFilesOrClickTo')}</p>
                    <span className={styles['file-hint']}>{tr('components.JobMediaManager.browse')}</span>
                  </IonCardContent>
                </IonCard>
              )}
            </IonCardContent>
          </IonCard>

          {/* Upload Progress - displayed OUTSIDE upload-area card */}
          {uploadingFiles.length > 0 && (
            <UploadProgress
              uploadingFiles={uploadingFiles}
              isVisible={true}
              onCancel={(index) => {
                setUploadingFiles(prev => prev.filter((_, i) => i !== index));
                if (uploadingFiles.length === 1) {
                  setUploading(false);
                }
              }}
              onRemove={(index) => {
                setUploadingFiles(prev => prev.filter((_, i) => i !== index));
                if (uploadingFiles.length === 1) {
                  setUploading(false);
                }
              }}
              onRetry={(index) => {
                const file = uploadingFiles[index]?.file;
                if (file) {
                  setUploadingFiles(prev => {
                    const updated = [...prev];
                    updated[index] = {
                      ...updated[index],
                      progress: 0,
                      status: 'uploading' as const,
                      error: undefined
                    };
                    return updated;
                  });
                  upload(file).then(() => {
                    setUploadingFiles(prev => {
                      const updated = [...prev];
                      updated[index] = { ...updated[index], status: 'completed' as const, progress: 100 };
                      return updated;
                    });
                  }).catch(err => {
                    setUploadingFiles(prev => {
                      const updated = [...prev];
                      updated[index] = {
                        ...updated[index],
                        status: 'error' as const,
                        error: String(err)
                      };
                      return updated;
                    });
                  });
                }
              }}
              onClearCompleted={() => {
                setUploadingFiles(prev => prev.filter(f => f.status !== 'completed'));
              }}
            />
          )}

          {/* Media Gallery - show saved files as list with small thumbnails */}
          {uploadingFiles.length === 0 && media.length > 0 && (
            <div className={styles['recent-uploads-container']}>
              <div className={styles['recent-uploads-header']}>
                <span
                  className={`${styles['recent-uploads-title']} ${selectionMode ? styles['clickable-text'] : ''}`}
                  onClick={selectionMode ? cancelSelection : undefined}
                >
                  {selectionMode ? tr('components.JobMediaManager.cancel') : tr('components.JobMediaManager.recentlyUploaded')}
                </span>
                {selectionMode && (
                  <span className={styles['selection-count']}>{tr('components.JobMediaManager.selected', { size: selectedIds.size })}</span>
                )}
                <span
                  className={selectionMode ? styles['selection-delete-text'] : styles['recent-uploads-count']}
                  onClick={selectionMode ? handleBulkDelete : undefined}
                >
                  {selectionMode ? tr('components.JobMediaManager.delete') : `${media.length} files`}
                </span>
              </div>

              <div className={styles['recent-uploads-list']}>
                {media.map((item) => (
                  <div
                    key={item._id}
                    className={`${styles['recent-upload-item']} ${selectionMode && item._id && selectedIds.has(item._id) ? styles['selected'] : ''}`}
                    onPointerDown={() => item._id && handlePointerDown(item._id)}
                    onPointerUp={handlePointerUp}
                    onPointerLeave={handlePointerUp}
                    onClick={() => {
                      if (longPressTriggered.current) {
                        longPressTriggered.current = false;
                        return;
                      }
                      if (selectionMode) {
                        if (item._id) toggleSelection(item._id);
                      } else {
                        setSelectedMedia(item);
                        setEditDescription(item.description || '');
                        setEditFileName(item.fileName || '');
                        setShowDetails(true);
                      }
                    }}
                  >
                    <div className={styles['recent-upload-thumbnail']}>
                      {item.mediaType?.toLowerCase() === 'image' || item.mimeType?.startsWith('image/') ? (
                        <img src={item.fileUrl} alt={item.fileName} />
                      ) : (
                        <span className={styles['recent-upload-icon']}>{getMediaIcon(item.mediaType)}</span>
                      )}
                    </div>
                    <div className={styles['recent-upload-info']}>
                      <p className={styles['recent-upload-name']}>{item.fileName}</p>
                      <p className={styles['recent-upload-meta']}>
                        {item.uploadedAt && !isNaN(new Date(item.uploadedAt).getTime()) && (
                          <span>{new Date(item.uploadedAt).toLocaleDateString(intlLocale())}</span>
                        )}
                      </p>
                    </div>
                    {selectionMode ? (
                      <div className={styles['selection-indicator']}>
                        <IonIcon
                          icon={item._id && selectedIds.has(item._id) ? checkmark : undefined}
                          className={`${styles['selection-circle']} ${item._id && selectedIds.has(item._id) ? styles['checked'] : ''}`}
                        />
                      </div>
                    ) : (
                      <IonButton
                        fill="clear"
                        size="small"
                        color="danger"
                        className={styles['recent-upload-delete']}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (item._id) handleDelete(item._id, item.fileName);
                        }}
                      >
                        <IonIcon icon={trash} />
                      </IonButton>
                    )}
                  </div>
                ))}
              </div>

              {/* Statistics Footer */}
              {stats && stats.totalCount > 0 && (
                <div className={styles['stats-footer']}>
                  <span>
                    <strong>{tr('components.JobMediaManager.types')} </strong>
                    {Object.entries(stats.byType)
                      .map(([type, count]) => `${type}: ${count}`)
                      .join(', ')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Loading State — only on initial fetch, not during uploads */}
          {loading && !media.length && uploadingFiles.length === 0 && (
            <IonItem lines="none">
              <IonSpinner slot="start" name="bubbles" />
              <IonLabel>{tr('components.JobMediaManager.loadingMedia')}</IonLabel>
            </IonItem>
          )}

          {/* Empty State */}
          {!loading && media.length === 0 && (
            <IonItem lines="none">
              <IonIcon icon={imageOutline} slot="start" size="large" />
              <IonLabel>{tr('components.JobMediaManager.noMediaAttachedToThisJob')}</IonLabel>
            </IonItem>
          )}
        </IonCardContent>
      </IonCard>

      {/* Image Preview — mobile: full-screen modal | desktop: centered lightbox */}
      {isMobile ? (
        <IonModal isOpen={showDetails} onDidDismiss={() => { setShowDetails(false); setDescriptionActive(false); }} className={styles['image-modal']}>
          <IonHeader>
            <IonToolbar />
          </IonHeader>

          {keyboardHeight > 0 && textareaFocused && (
            <div className={styles['keyboard-save-bar']}>
              <IonButton
                fill="clear"
                className={styles.btnGlassDark}
                onPointerDown={async (e) => {
                  e.preventDefault();
                  const el = await descriptionRef.current?.getInputElement();
                  if (el) {
                    const start = el.selectionStart ?? el.value.length;
                    const end = el.selectionEnd ?? el.value.length;
                    const v = el.value;
                    const next = v.slice(0, start) + '\n' + v.slice(end);
                    setEditDescription(next);
                    requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = start + 1; });
                  } else {
                    setEditDescription(v => v + '\n');
                  }
                }}
              >
                <IonIcon slot="start" icon={returnDownBack} />{tr('components.JobMediaManager.newLine')}
              </IonButton>
            </div>
          )}

          <IonContent scrollY={true} ref={modalContentRef}>
            {selectedMedia && (
              <>
                <IonCard className={styles['preview-section']}>
                  <IonButton fill="clear" className={styles['modal-close-btn']} onClick={() => setShowDetails(false)}>
                    <IonIcon slot="icon-only" icon={closeCircleOutline} />
                  </IonButton>
                  {selectedMedia.mediaType === 'image' && (
                    <img src={selectedMedia.fileUrl} alt={selectedMedia.fileName} loading="eager" className={styles['full-preview']} />
                  )}
                  {selectedMedia.mediaType === 'video' && (
                    <>
                      {(!videoBlobUrl || !videoReady) && !videoError && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                          <IonSpinner name="bubbles" />
                        </div>
                      )}
                      {videoBlobUrl && (
                        <video controls playsInline preload="metadata" src={videoBlobUrl}
                          className={styles['video-full-preview']}
                          style={{ opacity: videoReady ? 1 : 0, position: videoReady ? undefined : 'absolute', pointerEvents: videoReady ? undefined : 'none' }}
                          onError={() => { setVideoError(tr('components.JobMediaManager.unableToPlayThisVideo')); setShowVideoToast(true); }}
                          onLoadedMetadata={(e) => { e.currentTarget.currentTime = 0.001; }}
                          onSeeked={() => setVideoReady(true)}
                        />
                      )}
                    </>
                  )}
                  {selectedMedia.mediaType === 'document' && (
                    <IonItem lines="none" className={styles['document-preview']}>
                      <IonIcon icon={documentOutline} slot="start" size="large" color="primary" />
                      <IonLabel className="ion-text-wrap">{selectedMedia.fileName}</IonLabel>
                    </IonItem>
                  )}
                </IonCard>

                <IonCard className={styles['details-card']} style={{ paddingBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : undefined }}>
                  <IonCardHeader>
                    <IonInput value={editFileName}
                      onIonInput={e => setEditFileName((e.target as HTMLIonInputElement).value as string || '')}
                      onIonFocus={e => (e.target as unknown as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' })}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleUpdateDescription(); } }}
                      enterkeyhint="done" className={styles['filename-input']} />
                  </IonCardHeader>
                  <IonCardContent>
                    {(() => {
                      let meta: Record<string, string> = {};
                      try { meta = JSON.parse(selectedMedia.metadata || '{}'); } catch { /* ignore */ }
                      const tags = selectedMedia.tags ? selectedMedia.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
                      const dateTime = [meta.uploadDate, meta.uploadTime].filter(Boolean).join(' · ') || tags.slice(-3).join(' · ');
                      const typeLabel = meta.type ? meta.type.charAt(0).toUpperCase() + meta.type.slice(1) : '';
                      const formatSize = [typeLabel, meta.format, meta.fileSize].filter(Boolean).join(' · ') || tags.slice(0, 3).join(' · ');
                      const updatedAt = meta.updatedTimestamp ? new Date(meta.updatedTimestamp) : null;
                      const updatedLabel = updatedAt && !isNaN(updatedAt.getTime())
                        ? tr('components.JobMediaManager.updated', { v1: updatedAt.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', year: 'numeric' }), v2: updatedAt.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit' }) })
                        : null;
                      return (
                        <div className={styles['detail-meta']}>
                          {dateTime && <label className={styles['detail-meta-line']}>{dateTime}</label>}
                          {updatedLabel && <label className={styles['detail-meta-line']}>{updatedLabel}</label>}
                          {formatSize && <label className={styles['detail-meta-line']}>{formatSize}</label>}
                        </div>
                      );
                    })()}
                    {descriptionActive ? (
                      <IonTextarea ref={descriptionRef} value={editDescription}
                        onIonInput={e => setEditDescription((e.target as HTMLIonTextareaElement).value || '')}
                        onIonFocus={e => { setTextareaFocused(true); (e.target as unknown as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                        onIonBlur={() => setTextareaFocused(false)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleUpdateDescription(); setDescriptionActive(false); } }}
                        enterkeyhint="done" placeholder={tr('components.JobMediaManager.addADescription')} autoGrow className={styles.descriptionInput} />
                    ) : (
                      <div className={styles.descriptionTap} onClick={() => setDescriptionActive(true)}>
                        <span className={styles.descriptionTapText}>
                          {editDescription || <span className={styles.descriptionPlaceholder}>{tr('components.JobMediaManager.addADescription2')}</span>}
                        </span>
                        <IonIcon icon={createOutline} className={styles.descriptionEditIcon} />
                      </div>
                    )}
                    <IonRow className={styles['detail-actions']}>
                      <IonCol>
                        <IonButton expand="block" fill="clear" className={styles.btnGlassPrimary} onClick={handleUpdateDescription}>
                          <IonIcon slot="start" icon={checkmark} />{tr('components.JobMediaManager.save')}
                        </IonButton>
                      </IonCol>
                      <IonCol>
                        <IonButton expand="block" fill="clear" className={styles.btnGlassDark} onClick={async () => {
                          if (selectedMedia?.fileUrl) {
                            try {
                              const r = await fetch(selectedMedia.fileUrl);
                              const blob = await r.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a'); a.href = url; a.download = selectedMedia.fileName || 'download';
                              document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                            } catch { window.open(selectedMedia.fileUrl, '_blank'); }
                          }
                        }}>
                          <IonIcon slot="start" icon={download} />{tr('components.JobMediaManager.download')}
                        </IonButton>
                      </IonCol>
                      <IonCol>
                        <IonButton expand="block" fill="clear" className={styles.btnGlassDanger} onClick={() => {
                          if (selectedMedia?._id) { handleDelete(selectedMedia._id, selectedMedia.fileName); setShowDetails(false); }
                        }}>
                          <IonIcon slot="start" icon={trash} />{tr('components.JobMediaManager.delete')}
                        </IonButton>
                      </IonCol>
                    </IonRow>
                  </IonCardContent>
                </IonCard>
              </>
            )}
          </IonContent>
        </IonModal>
      ) : showDetails && selectedMedia ? (
        /* Desktop lightbox — centered overlay with image + details side by side */
        <div className={styles.desktopLightbox} onClick={() => { setShowDetails(false); setDescriptionActive(false); }}>
          <div className={styles.desktopLightboxDialog} onClick={e => e.stopPropagation()}>

            {/* Preview pane */}
            <div className={styles.desktopLightboxPreview}>
              {selectedMedia.mediaType === 'image' && (
                <img src={selectedMedia.fileUrl} alt={selectedMedia.fileName} />
              )}
              {selectedMedia.mediaType === 'video' && (
                <>
                  {(!videoBlobUrl || !videoReady) && !videoError && (
                    <IonSpinner name="bubbles" style={{ color: '#fff' }} />
                  )}
                  {videoBlobUrl && (
                    <video controls playsInline preload="metadata" src={videoBlobUrl}
                      style={{ opacity: videoReady ? 1 : 0 }}
                      onError={() => { setVideoError(tr('components.JobMediaManager.unableToPlayThisVideo')); setShowVideoToast(true); }}
                      onLoadedMetadata={e => { e.currentTarget.currentTime = 0.001; }}
                      onSeeked={() => setVideoReady(true)}
                    />
                  )}
                </>
              )}
              {selectedMedia.mediaType === 'document' && (
                <div className={styles.desktopLightboxDocIcon}>
                  <IonIcon icon={documentOutline} />
                  <span>{selectedMedia.fileName}</span>
                </div>
              )}
            </div>

            {/* Details pane */}
            <div className={styles.desktopLightboxDetails}>
              <button className={styles.desktopLightboxCloseBtn} onClick={() => { setShowDetails(false); setDescriptionActive(false); }}>
                <IonIcon icon={closeCircleOutline} />
              </button>

              <IonInput value={editFileName}
                onIonInput={e => setEditFileName((e.target as HTMLIonInputElement).value as string || '')}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleUpdateDescription(); } }}
                enterkeyhint="done"
                className={styles.desktopLightboxFilename}
              />

              {(() => {
                let meta: Record<string, string> = {};
                try { meta = JSON.parse(selectedMedia.metadata || '{}'); } catch { /* ignore */ }
                const tags = selectedMedia.tags ? selectedMedia.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
                const dateTime = [meta.uploadDate, meta.uploadTime].filter(Boolean).join(' · ') || tags.slice(-3).join(' · ');
                const typeLabel = meta.type ? meta.type.charAt(0).toUpperCase() + meta.type.slice(1) : '';
                const formatSize = [typeLabel, meta.format, meta.fileSize].filter(Boolean).join(' · ') || tags.slice(0, 3).join(' · ');
                const updatedAt = meta.updatedTimestamp ? new Date(meta.updatedTimestamp) : null;
                const updatedLabel = updatedAt && !isNaN(updatedAt.getTime())
                  ? tr('components.JobMediaManager.updated2', { v1: updatedAt.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', year: 'numeric' }) })
                  : null;
                return (
                  <div className={styles['detail-meta']}>
                    {dateTime && <label className={styles['detail-meta-line']}>{dateTime}</label>}
                    {updatedLabel && <label className={styles['detail-meta-line']}>{updatedLabel}</label>}
                    {formatSize && <label className={styles['detail-meta-line']}>{formatSize}</label>}
                  </div>
                );
              })()}

              {descriptionActive ? (
                <IonTextarea ref={descriptionRef} value={editDescription}
                  onIonInput={e => setEditDescription((e.target as HTMLIonTextareaElement).value || '')}
                  onIonBlur={() => { setTextareaFocused(false); setDescriptionActive(false); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleUpdateDescription(); setDescriptionActive(false); } }}
                  enterkeyhint="done" placeholder={tr('components.JobMediaManager.addADescription')} autoGrow className={styles.descriptionInput} />
              ) : (
                <div className={styles.descriptionTap} onClick={() => setDescriptionActive(true)}>
                  <span className={styles.descriptionTapText}>
                    {editDescription || <span className={styles.descriptionPlaceholder}>{tr('components.JobMediaManager.addADescription2')}</span>}
                  </span>
                  <IonIcon icon={createOutline} className={styles.descriptionEditIcon} />
                </div>
              )}

              <div className={styles.desktopLightboxActions}>
                <IonButton expand="block" fill="clear" className={styles.btnGlassPrimary} onClick={handleUpdateDescription}>
                  <IonIcon slot="start" icon={checkmark} />{tr('components.JobMediaManager.save')}
                </IonButton>
                <IonButton expand="block" fill="clear" className={styles.btnGlassDark} onClick={async () => {
                  if (selectedMedia?.fileUrl) {
                    try {
                      const r = await fetch(selectedMedia.fileUrl);
                      const blob = await r.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = selectedMedia.fileName || 'download';
                      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                    } catch { window.open(selectedMedia.fileUrl, '_blank'); }
                  }
                }}>
                  <IonIcon slot="start" icon={download} />{tr('components.JobMediaManager.download')}
                </IonButton>
                <IonButton expand="block" fill="clear" className={styles.btnGlassDanger} onClick={() => {
                  if (selectedMedia?._id) { handleDelete(selectedMedia._id, selectedMedia.fileName); setShowDetails(false); }
                }}>
                  <IonIcon slot="start" icon={trash} />{tr('components.JobMediaManager.delete')}
                </IonButton>
              </div>
            </div>

          </div>
        </div>
      ) : null}
      <IonLoading isOpen={deleting} cssClass="job-media-loading" />
      <IonToast
        isOpen={deleteToast.isOpen}
        message={deleteToast.message}
        duration={2500}
        onDidDismiss={() => setDeleteToast({ isOpen: false, message: '' })}
        position="bottom"
        cssClass="job-media-toast"
        icon={checkmarkCircle}
      />

      {/* Video Error Toast */}
      <IonToast
        isOpen={showVideoToast}
        onDidDismiss={() => setShowVideoToast(false)}
        message={videoError ?? ''}
        duration={3000}
        color="danger"
      />

      {/* Delete Confirmation Alert */}
      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => {
          setShowDeleteAlert(false);
          setDeleteTargetId(null);
          setDeleteTargetName('');
        }}
        cssClass="job-media-delete-alert"
        header={selectionMode ? tr('components.JobMediaManager.deleteFile', { size: selectedIds.size, v2: selectedIds.size > 1 ? 's' : '' }) : tr('components.JobMediaManager.deleteThisMediaFile')}
        subHeader={selectionMode ? undefined : truncateFilename(deleteTargetName)}
        message={selectionMode ? tr('components.JobMediaManager.allSelectedFilesWillBePermanently') : tr('components.JobMediaManager.youReAboutToPermanentlyDelete')}
        buttons={[
          {
            text: tr('components.JobMediaManager.cancel'),
            role: 'cancel'
          },
          {
            text: tr('components.JobMediaManager.delete'),
            role: 'destructive',
            handler: confirmDelete
          }
        ]}
      />

    </>
  );
};
